import React, { useState } from 'react';
import { usePersistentState } from '../lib/usePersistentState';
import { useUserData } from '../context/UserDataContext';
import { ArrowLeft, TrendingUp, Plus, X, Trash2 } from 'lucide-react';
import EmptyState from './EmptyState';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const pct = (a, b) => Math.min(Math.round((a / b) * 100), 100);
const monthsUntil = (d) => {
  const diff = new Date(d) - new Date();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24 * 30)));
};

const GOAL_ICONS = ['🏠', '🚗', '✈️', '🎓', '💍', '🛡️', '📈', '💰', '🏖️', '🎯', '🏋️', '🌍'];

// ── Housing drill-down ────────────────────────────────────────────────────────
function HousingDetail({ goal, onBack }) {
  const [homePrice, setHomePrice] = useState(550000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.75);
  const [term, setTerm] = useState(30);

  const downAmt = homePrice * (downPct / 100);
  const loanAmt = homePrice - downAmt;
  const monthlyRate = rate / 100 / 12;
  const n = term * 12;
  const pmt = loanAmt * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
  const pmi = downPct < 20 ? Math.round(loanAmt * 0.005 / 12) : 0;

  const savingsVehicles = [
    { name: 'HYSA', apy: '4.8%', note: 'Highest liquidity, FDIC insured', best: true },
    { name: '1-Year CD', apy: '5.1%', note: 'Locked for 12 months, good if 1+ yr away' },
    { name: 'T-Bills (4-wk)', apy: '5.2%', note: 'State tax exempt, rolls every 4 weeks' },
    { name: 'Short Bond ETF', apy: '5.0%', note: 'VGSH/SHY, slight market risk' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors">
          <ArrowLeft size={16} className="text-slate-400" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-slate-100">🏠 Home Down Payment</h2>
          <p className="text-slate-400 text-sm">Mortgage calculator + savings strategy</p>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex justify-between mb-2">
          <span className="text-sm text-slate-400">Progress to goal</span>
          <span className="text-sm font-bold text-slate-200">{fmt(goal.current)} / {fmt(goal.target)}</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-3 mb-2">
          <div className="bg-blue-500 h-3 rounded-full" style={{ width: `${pct(goal.current, goal.target)}%` }} />
        </div>
        <div className="text-xs text-slate-500">{fmt(goal.target - goal.current)} remaining</div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-slate-200 mb-4">Live Mortgage Calculator</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {[
            { label: `Home Price: ${fmt(homePrice)}`, min: 200000, max: 1500000, step: 10000, val: homePrice, set: setHomePrice },
            { label: `Down Payment: ${downPct}% (${fmt(downAmt)})`, min: 3, max: 40, step: 1, val: downPct, set: setDownPct },
            { label: `Interest Rate: ${rate}%`, min: 3, max: 12, step: 0.125, val: rate, set: setRate },
            { label: `Term: ${term} years`, min: 15, max: 30, step: 5, val: term, set: setTerm },
          ].map(s => (
            <div key={s.label}>
              <div className="text-xs text-slate-400 mb-1">{s.label}</div>
              <input type="range" min={s.min} max={s.max} step={s.step} value={s.val}
                onChange={e => s.set(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-blue-500" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { label: 'Loan Amount', value: fmt(loanAmt) },
            { label: 'Monthly P&I', value: fmt(Math.round(pmt)), highlight: true },
            { label: pmi > 0 ? `+PMI ${fmt(pmi)}/mo` : 'No PMI ✓', value: pmi > 0 ? fmt(Math.round(pmt) + pmi) : fmt(Math.round(pmt)), color: pmi > 0 ? 'text-amber-400' : 'text-emerald-400' },
          ].map(k => (
            <div key={k.label} className={`p-3 rounded-lg ${k.highlight ? 'bg-blue-900/40 border border-blue-700/40' : 'bg-slate-700/40'}`}>
              <div className="text-xs text-slate-400">{k.label}</div>
              <div className={`text-lg font-bold mt-1 ${k.color || 'text-slate-200'}`}>{k.value}</div>
            </div>
          ))}
        </div>
        {pmi > 0 && (
          <div className="mt-3 p-2 bg-amber-950/40 border border-amber-700/30 rounded-lg text-xs text-amber-300">
            ⚠ PMI required when down payment &lt; 20%. Costs an extra {fmt(pmi)}/mo until you reach 20% equity.
          </div>
        )}
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-slate-200 mb-4">Where to Save Your Down Payment</h3>
        <div className="grid grid-cols-2 gap-3">
          {savingsVehicles.map(v => (
            <div key={v.name} className={`p-4 rounded-xl border ${v.best ? 'border-emerald-700/50 bg-emerald-950/30' : 'border-slate-700/50'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-slate-200">{v.name}</span>
                {v.best && <span className="text-xs bg-emerald-700/40 text-emerald-300 rounded-full px-1.5 py-0.5">Best</span>}
              </div>
              <div className="text-xl font-bold text-emerald-400 mb-1">{v.apy} APY</div>
              <div className="text-xs text-slate-400">{v.note}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-slate-200 mb-4">Current Mortgage Rates</h3>
        <div className="space-y-2">
          {[
            { term: '30-Year Fixed', rate: '6.75%', pmt: Math.round((loanAmt * (6.75/100/12) * Math.pow(1 + 6.75/100/12, 360)) / (Math.pow(1 + 6.75/100/12, 360) - 1)) },
            { term: '20-Year Fixed', rate: '6.35%', pmt: Math.round((loanAmt * (6.35/100/12) * Math.pow(1 + 6.35/100/12, 240)) / (Math.pow(1 + 6.35/100/12, 240) - 1)) },
            { term: '15-Year Fixed', rate: '6.05%', pmt: Math.round((loanAmt * (6.05/100/12) * Math.pow(1 + 6.05/100/12, 180)) / (Math.pow(1 + 6.05/100/12, 180) - 1)) },
            { term: '5/1 ARM', rate: '6.10%', pmt: Math.round((loanAmt * (6.10/100/12) * Math.pow(1 + 6.10/100/12, 360)) / (Math.pow(1 + 6.10/100/12, 360) - 1)), note: 'Adjusts after 5 yrs — risky' },
          ].map(r => (
            <div key={r.term} className="flex items-center justify-between py-2 border-b border-slate-700/40 last:border-0">
              <div>
                <span className="text-sm text-slate-300">{r.term}</span>
                {r.note && <span className="text-xs text-amber-400 ml-2">· {r.note}</span>}
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-slate-200">{r.rate}</span>
                <span className="text-xs text-slate-500 ml-2">{fmt(r.pmt)}/mo</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 p-2 bg-blue-950/30 border border-blue-700/30 rounded-lg text-xs text-blue-300">
          1% higher rate on {fmt(loanAmt)} loan = {fmt(Math.round(loanAmt * 0.006))}/year extra · {fmt(Math.round(loanAmt * 0.006 * 30))} over 30 years
        </div>
      </div>
    </div>
  );
}

// ── Generic goal detail ───────────────────────────────────────────────────────
function GoalDetail({ goal, onBack }) {
  const [months, setMonths] = useState(12);
  const remaining = goal.target - goal.current;
  const perMonth = Math.ceil(remaining / months);

  const vehicles = {
    emergency: [
      { name: 'HYSA', note: 'Must stay liquid — no CDs or bonds for emergency fund', apy: '4.8%', best: true },
      { name: 'Money Market', note: 'Check writing, FDIC insured, slightly lower yield', apy: '4.6%' },
    ],
    travel: [
      { name: 'HYSA', note: 'Short timeline — keep it simple and liquid', apy: '4.8%', best: true },
      { name: 'Money Market', note: 'Good for 6-18 month goals', apy: '4.6%' },
    ],
    car: [
      { name: 'HYSA', note: 'Flexible timeline, easy to access', apy: '4.8%' },
      { name: '1-Year CD', note: 'If 12+ months away, lock in higher rate', apy: '5.1%', best: true },
    ],
  };

  const tips = {
    emergency: [
      '3-6 months of essential expenses is the target',
      "Keep in a separate account so you're not tempted to spend it",
      'Replenish within 3 months if you draw from it',
    ],
    travel: [
      'Book flights 4-6 months in advance for best fares',
      'Travel in shoulder season (Apr-May, Sep-Oct) for 20-40% savings',
      'Use a travel card to earn points on everyday spending',
    ],
    car: [
      'Get pre-approved at your credit union before visiting a dealer',
      'A certified pre-owned vehicle (2-3 yr old) often gives best value',
      'Budget 15% of purchase price for tax, title, registration, and first year insurance',
    ],
  };

  const categoryVehicles = vehicles[goal.category] || vehicles.travel;
  const categoryTips = tips[goal.category] || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors">
          <ArrowLeft size={16} className="text-slate-400" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-slate-100">{goal.icon} {goal.name}</h2>
          <p className="text-slate-400 text-sm">{goal.vehicle}</p>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex justify-between mb-2">
          <span className="text-sm text-slate-400">Progress</span>
          <span className="text-sm font-bold">{fmt(goal.current)} / {fmt(goal.target)}</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-3 mb-2">
          <div className={`h-3 rounded-full ${pct(goal.current, goal.target) >= 80 ? 'bg-emerald-500' : 'bg-blue-500'}`}
            style={{ width: `${pct(goal.current, goal.target)}%` }} />
        </div>
        <div className="text-xs text-slate-500">
          {fmt(remaining)} remaining
          {goal.targetDate && ` · Target: ${new Date(goal.targetDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-slate-200 mb-4">How much to save per month?</h3>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[3, 6, 12, 18].map(m => (
            <button key={m} onClick={() => setMonths(m)}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${months === m ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-slate-200'}`}>
              {m} mo
            </button>
          ))}
        </div>
        <div className="p-4 bg-blue-900/30 border border-blue-700/40 rounded-xl text-center">
          <div className="text-3xl font-black text-blue-300">{fmt(perMonth)}</div>
          <div className="text-sm text-blue-200/60 mt-1">per month to reach goal in {months} months</div>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-slate-200 mb-4">Recommended Savings Vehicle</h3>
        <div className="grid grid-cols-2 gap-3">
          {categoryVehicles.map(v => (
            <div key={v.name} className={`p-4 rounded-xl border ${v.best ? 'border-emerald-700/50 bg-emerald-950/30' : 'border-slate-700/50'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-slate-200">{v.name}</span>
                {v.best && <span className="text-xs bg-emerald-700/40 text-emerald-300 rounded-full px-1.5">Best</span>}
              </div>
              <div className="text-xl font-bold text-emerald-400 mb-1">{v.apy} APY</div>
              <div className="text-xs text-slate-400">{v.note}</div>
            </div>
          ))}
        </div>
      </div>

      {categoryTips.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="font-semibold text-slate-200 mb-3">Tips</h3>
          <ul className="space-y-2">
            {categoryTips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                <TrendingUp size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Add Goal Modal ────────────────────────────────────────────────────────────
function AddGoalModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('🎯');
  const [target, setTarget] = useState('');
  const [current, setCurrent] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [category, setCategory] = useState('travel');
  const [vehicle, setVehicle] = useState('HYSA');

  const handleSubmit = (e) => {
    e.preventDefault();
    const t = parseFloat(target.replace(/[^0-9.]/g, ''));
    const c = parseFloat(current.replace(/[^0-9.]/g, '')) || 0;
    if (!name.trim() || isNaN(t) || t <= 0) return;
    onAdd({
      id: crypto.randomUUID(),
      name: name.trim(),
      icon,
      target: t,
      current: c,
      targetDate: targetDate || null,
      category,
      vehicle,
      monthlyNeeded: targetDate ? Math.ceil((t - c) / Math.max(1, monthsUntil(targetDate))) : 0,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Add Goal</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Icon picker */}
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Icon</label>
            <div className="flex flex-wrap gap-2">
              {GOAL_ICONS.map(em => (
                <button
                  key={em}
                  type="button"
                  onClick={() => setIcon(em)}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-colors ${icon === em ? 'bg-blue-600 border border-blue-500' : 'bg-slate-700 border border-slate-600 hover:bg-slate-600'}`}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Goal Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Emergency Fund"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Target Amount</label>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-sm">$</span>
                <input
                  value={target}
                  onChange={e => setTarget(e.target.value)}
                  placeholder="10,000"
                  type="number"
                  min="1"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Current Savings</label>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-sm">$</span>
                <input
                  value={current}
                  onChange={e => setCurrent(e.target.value)}
                  placeholder="0"
                  type="number"
                  min="0"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Target Date (optional)</label>
              <input
                type="date"
                value={targetDate}
                onChange={e => setTargetDate(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="housing">Housing</option>
                <option value="emergency">Emergency Fund</option>
                <option value="travel">Travel</option>
                <option value="car">Car</option>
                <option value="education">Education</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Savings Vehicle</label>
            <input
              value={vehicle}
              onChange={e => setVehicle(e.target.value)}
              placeholder="HYSA, Brokerage, etc."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
            >
              Add Goal
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Goals list ────────────────────────────────────────────────────────────────
export default function Goals() {
  const { goalsData, updateSection } = useUserData();
  const goals = goalsData || [];

  const [drilldown, setDrilldown] = usePersistentState('finovo_goals_drilldown', null);
  const [showAddModal, setShowAddModal] = useState(false);

  const selected = drilldown ? goals.find(g => g.id === drilldown) : null;

  const handleAdd = (newGoal) => {
    updateSection('goals', [...goals, newGoal]);
  };

  const handleDelete = (e, goalId) => {
    e.stopPropagation();
    updateSection('goals', goals.filter(g => g.id !== goalId));
  };

  if (drilldown && selected) {
    if (selected.category === 'housing') return (
      <div className="p-6 max-w-5xl mx-auto">
        <HousingDetail goal={selected} onBack={() => setDrilldown(null)} />
      </div>
    );
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <GoalDetail goal={selected} onBack={() => setDrilldown(null)} />
      </div>
    );
  }

  const totalSaved  = goals.reduce((s, g) => s + g.current, 0);
  const totalTarget = goals.reduce((s, g) => s + g.target, 0);

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {showAddModal && (
        <AddGoalModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Goals</h1>
          <p className="text-slate-400 text-sm mt-0.5">Track progress toward your financial milestones</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={16} /> Add Goal
        </button>
      </div>

      {goals.length === 0 ? (
        <EmptyState
          icon="🎯"
          title="No goals yet"
          description="Set financial goals to track your progress toward the things that matter most."
          actionLabel="Add Your First Goal"
          onAction={() => setShowAddModal(true)}
        />
      ) : (
        <>
          {/* Summary */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex justify-between mb-2">
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">Total across all goals</div>
                <div className="text-2xl font-bold text-slate-100 mt-1">
                  {fmt(totalSaved)} <span className="text-slate-500 text-base font-normal">/ {fmt(totalTarget)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400 uppercase tracking-wider">Overall progress</div>
                <div className="text-2xl font-bold text-blue-400 mt-1">{totalTarget > 0 ? pct(totalSaved, totalTarget) : 0}%</div>
              </div>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-400 h-2 rounded-full"
                style={{ width: `${totalTarget > 0 ? pct(totalSaved, totalTarget) : 0}%` }}
              />
            </div>
          </div>

          {/* Goal cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {goals.map(goal => {
              const p = pct(goal.current, goal.target);
              const mo = goal.targetDate ? monthsUntil(goal.targetDate) : null;
              const color = p >= 80 ? 'bg-emerald-500' : p >= 50 ? 'bg-blue-500' : 'bg-amber-500';
              const textColor = p >= 80 ? 'text-emerald-400' : p >= 50 ? 'text-blue-400' : 'text-amber-400';

              return (
                <div key={goal.id} className="relative group">
                  <button
                    onClick={() => setDrilldown(goal.id)}
                    className="w-full bg-slate-800 border border-slate-700 hover:border-blue-600/50 rounded-xl p-5 text-left transition-all hover:bg-slate-750"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{goal.icon}</span>
                        <div>
                          <div className="font-semibold text-slate-200 text-sm group-hover:text-blue-300 transition-colors">{goal.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {mo !== null ? `${mo} months until target` : 'No target date'}
                          </div>
                        </div>
                      </div>
                      <span className={`text-lg font-black ${textColor}`}>{p}%</span>
                    </div>

                    <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
                      <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${p}%` }} />
                    </div>

                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">{fmt(goal.current)} saved</span>
                      <span className="text-slate-500">of {fmt(goal.target)}</span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-700/50 flex justify-between text-xs">
                      <span className="text-slate-500">{goal.vehicle || 'No vehicle set'}</span>
                      <span className="text-blue-400 group-hover:text-blue-300">View details →</span>
                    </div>
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, goal.id)}
                    className="absolute top-3 right-3 p-1.5 rounded-lg bg-slate-700 hover:bg-rose-900/60 border border-slate-600 hover:border-rose-700/50 opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete goal"
                  >
                    <Trash2 size={12} className="text-slate-400 hover:text-rose-400" />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
